
import {ExportRegistry} from "./ExportRegistry.js";
import {buildExportFilename} from "./exportFilename.js";

import {T, t} from "../i18n/index.js";
// Task section 7/8/20 audit finding: DXF was previously a StubExporter
// (disabled:true, throws if invoked) - genuinely unimplemented, not
// disabled-but-built or broken (see StubExporters.js's own history). This is
// a real, from-scratch implementation - DXF's ASCII text structure (group
// code / value line pairs) doesn't warrant an external library the way
// Shapefile's binary DBF/ZIP format does (see ShapefileFeatureExporter.js).
//
// Targets DXF R12 (AC1009). This file writes POINT, 3D POLYLINE and TEXT, all
// of which R12 has, and R12 needs no handles, subclass markers, owner
// references, BLOCKS or OBJECTS. It used to declare AC1021 with this same
// R12-shaped content, which R13+ does not allow: handles are mandatory there,
// and tolerant readers such as ezdxf only load such a file by repairing it.
const DXF_VERSION = "AC1009";

// R12 is not UTF-8: strings are in the declared code page. Windows-1252 covers
// da/de/sv/pt; everything else is written as \U+XXXX, which CAD readers decode
// themselves. The result therefore has to be returned as bytes, see encodeCp1252().
const DXF_CODE_PAGE = "ANSI_1252";
const CP1252_C1 = "€\u0000‚ƒ„…†‡ˆ‰Š‹Œ\u0000Ž\u0000\u0000‘’“”•–—˜™š›œ\u0000žŸ";
const CP1252_BYTES = new Map([...CP1252_C1].flatMap((ch, i) => (ch === "\u0000" ? [] : [[ch, 0x80 + i]])));

function cp1252Byte(unit){
	if(unit < 0x80 || (unit >= 0xA0 && unit <= 0xFF)) return unit;
	return CP1252_BYTES.get(String.fromCharCode(unit));
}

// Expects text already passed through dxfEscapeText/sanitizeLayerName, so every
// UTF-16 unit has a byte.
function encodeCp1252(text){
	const bytes = new Uint8Array(text.length);
	for(let i = 0; i < text.length; i++) bytes[i] = cp1252Byte(text.charCodeAt(i));
	return bytes;
}

// R12 limits a string group value to 255 characters.
const DXF_MAX_TEXT = 255;

function dxfEscapeText(value){
	// DXF text group codes: a literal newline inside the value would break
	// the group-code/value line pairing (the parser reads content up to the
	// next newline as this line's value) - collapse to a single line, same
	// "coordinate labels/exports are one line" convention already used by
	// this module's other exporters (CSVFeatureExporter's csvEscape).
	const text = String(value).replace(/[\r\n]+/g, " ").normalize("NFC");
	let escaped = "";
	for(let i = 0; i < text.length; i++){
		const unit = text.charCodeAt(i);
		// \U+ has exactly four hex digits, so non-BMP characters become their surrogate pair.
		const piece = cp1252Byte(unit) === undefined ? `\\U+${unit.toString(16).toUpperCase().padStart(4, "0")}` : text[i];
		if(escaped.length + piece.length > DXF_MAX_TEXT) break;
		escaped += piece;
	}
	return escaped;
}

// R12 symbol names: uppercase A-Z, 0-9, _ $ -, at most 31 characters. Danish
// letters are spelled out and other diacritics dropped, so "Grøft" stays
// readable as "GROEFT"; only the layer name changes, TEXT keeps the real name.
const DANISH_LETTERS = {Æ: "AE", Ø: "OE", Å: "AA"};

function sanitizeLayerName(name){
	const cleaned = String(name || t("Uncategorized"))
		.trim()
		.toUpperCase()
		.replace(/[ÆØÅ]/g, (ch) => DANISH_LETTERS[ch])
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.replace(/[^A-Z0-9_$-]/g, "_")
		.slice(0, 31);
	return cleaned.length > 0 ? cleaned : "UNCATEGORIZED";
}

function groupCode(code, value){
	return `${code}\r\n${value}\r\n`;
}

function polygonOpenRing(coords){
	if(coords.length < 2) return coords;
	const [x0, y0, z0] = coords[0];
	const [xn, yn, zn] = coords[coords.length - 1];
	const closesExactly = Math.abs(x0 - xn) < 1e-9 && Math.abs(y0 - yn) < 1e-9 && Math.abs((z0 || 0) - (zn || 0)) < 1e-9;
	return closesExactly ? coords.slice(0, -1) : coords;
}

function writePolyline(vertices, layer, closed){
	// Classic (non-lightweight) POLYLINE, not LWPOLYLINE: LWPOLYLINE only
	// carries a single constant elevation per entity (task's own geometry
	// audit requirement is per-vertex Z, real survey-derived data) - the
	// 3D-polyline flag (70 = 8) plus per-VERTEX 10/20/30 groups is the
	// correct DXF entity for a genuinely 3D polyline with varying Z.
	const flags = closed ? 8 | 1 : 8;
	let s = groupCode(0, "POLYLINE") + groupCode(8, layer) + groupCode(66, 1) +
		groupCode(10, 0) + groupCode(20, 0) + groupCode(30, 0) + groupCode(70, flags);
	for(const [x, y, z] of vertices){
		s += groupCode(0, "VERTEX") + groupCode(8, layer) + groupCode(10, x) + groupCode(20, y) + groupCode(30, z || 0) +
			groupCode(70, 32);
	}
	s += groupCode(0, "SEQEND") + groupCode(8, layer);
	return s;
}

function writeLabel(text, x, y, z, layer){
	if(!text) return "";
	return groupCode(0, "TEXT") + groupCode(8, layer) + groupCode(10, x) + groupCode(20, y) + groupCode(30, z || 0) +
		groupCode(40, 0.2) + groupCode(1, dxfEscapeText(text));
}

function featureToEntities(feature){
	const layer = sanitizeLayerName(feature.properties.category);
	const name = feature.properties.name;
	const type = feature.geometry.type;
	const coords = feature.geometry.coordinates;

	if(type === "Point"){
		const [x, y, z] = coords;
		return groupCode(0, "POINT") + groupCode(8, layer) + groupCode(10, x) + groupCode(20, y) + groupCode(30, z || 0) +
			writeLabel(name, x, y, z, layer);
	}
	if(type === "LineString"){
		if(coords.length < 2) return "";
		const [x, y, z] = coords[0];
		return writePolyline(coords, layer, false) + writeLabel(name, x, y, z, layer);
	}
	if(type === "Polygon"){
		const ring = polygonOpenRing(coords[0] || []);
		if(ring.length < 3) return "";
		const [x, y, z] = ring[0];
		return writePolyline(ring, layer, true) + writeLabel(name, x, y, z, layer);
	}
	return "";
}

function collectExtent(features){
	let xmin = Infinity, ymin = Infinity, zmin = Infinity, xmax = -Infinity, ymax = -Infinity, zmax = -Infinity;
	for(const f of features){
		const type = f.geometry.type;
		const coords = type === "Polygon" ? (f.geometry.coordinates[0] || []) : (type === "Point" ? [f.geometry.coordinates] : f.geometry.coordinates);
		for(const [x, y, z] of coords){
			const elevation = z || 0;
			if(x < xmin) xmin = x;
			if(x > xmax) xmax = x;
			if(y < ymin) ymin = y;
			if(y > ymax) ymax = y;
			if(elevation < zmin) zmin = elevation;
			if(elevation > zmax) zmax = elevation;
		}
	}
	if(xmin === Infinity) return {xmin: 0, ymin: 0, zmin: 0, xmax: 0, ymax: 0, zmax: 0};
	return {xmin, ymin, zmin, xmax, ymax, zmax};
}

export const DXFFeatureExporter = {
	id: "dxf",
	name: T("DXF"),
	extension: "dxf",
	disabled: false,
	description: T("AutoCAD DXF (R12) med højde (Z). Lag følger registreringernes kategori; navne skrives som tekst."),
	crsHandling: "none",
	// `context.projection`: task section 12 - DXF bevarer de faktiske
	// koordinater uden skjulte transformationer. This exporter never reads
	// or applies `context.projection` at all - every X/Y/Z is written
	// exactly as digitized, in whatever CRS the project's own coordinates
	// already are (DXF itself has no native CRS/prj-equivalent metadata
	// field the way Shapefile does, so there is nothing to (mis)declare).
	export(features, context = {}){
		// Layer 0 always exists in a drawing, and every layer's CONTINUOUS must be defined.
		const layers = [...new Set(["0", ...features.map((f) => sanitizeLayerName(f.properties.category))])];
		const extent = collectExtent(features);

		let dxf = "";
		dxf += groupCode(0, "SECTION") + groupCode(2, "HEADER") +
			groupCode(9, "$ACADVER") + groupCode(1, DXF_VERSION) +
			groupCode(9, "$DWGCODEPAGE") + groupCode(3, DXF_CODE_PAGE) +
			groupCode(9, "$INSBASE") + groupCode(10, 0) + groupCode(20, 0) + groupCode(30, 0) +
			groupCode(9, "$EXTMIN") + groupCode(10, extent.xmin) + groupCode(20, extent.ymin) + groupCode(30, extent.zmin) +
			groupCode(9, "$EXTMAX") + groupCode(10, extent.xmax) + groupCode(20, extent.ymax) + groupCode(30, extent.zmax) +
			groupCode(0, "ENDSEC");

		dxf += groupCode(0, "SECTION") + groupCode(2, "TABLES") +
			groupCode(0, "TABLE") + groupCode(2, "LTYPE") + groupCode(70, 1) +
			groupCode(0, "LTYPE") + groupCode(2, "CONTINUOUS") + groupCode(70, 0) + groupCode(3, "Solid line") +
			groupCode(72, 65) + groupCode(73, 0) + groupCode(40, 0) +
			groupCode(0, "ENDTAB");
		dxf += groupCode(0, "TABLE") + groupCode(2, "LAYER") + groupCode(70, layers.length);
		for(const layer of layers){
			dxf += groupCode(0, "LAYER") + groupCode(2, layer) + groupCode(70, 0) + groupCode(62, 7) + groupCode(6, "CONTINUOUS");
		}
		dxf += groupCode(0, "ENDTAB");
		dxf += groupCode(0, "TABLE") + groupCode(2, "STYLE") + groupCode(70, 1) +
			groupCode(0, "STYLE") + groupCode(2, "STANDARD") + groupCode(70, 0) + groupCode(40, 0) + groupCode(41, 1) +
			groupCode(50, 0) + groupCode(71, 0) + groupCode(42, 0.2) + groupCode(3, "txt") + groupCode(4, "") +
			groupCode(0, "ENDTAB") + groupCode(0, "ENDSEC");

		dxf += groupCode(0, "SECTION") + groupCode(2, "ENTITIES");
		for(const feature of features) dxf += featureToEntities(feature);
		dxf += groupCode(0, "ENDSEC") + groupCode(0, "EOF");

		const mimeType = "application/dxf";
		return {
			filename: buildExportFilename(context.pointcloudName, "dxf", context.exportDate),
			blob: new Blob([encodeCp1252(dxf)], {type: mimeType}),
			mimeType,
		};
	},
};

ExportRegistry.register(DXFFeatureExporter);
